import { useState, useEffect } from 'react';
import { FavoriteItem } from '../types';

// Local storage key for favorites
const FAVORITES_STORAGE_KEY = 'movies-series-favorites';

export const useFavorites = () => {
  // Initialize favorites from localStorage
  const [favorites, setFavorites] = useState<FavoriteItem[]>(() => {
    const savedFavorites = localStorage.getItem(FAVORITES_STORAGE_KEY);
    return savedFavorites ? JSON.parse(savedFavorites) : [];
  });

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
  }, [favorites]);

  // Add item to favorites
  const addToFavorites = (item: FavoriteItem) => {
    setFavorites(prev => {
      // Check if item already exists
      const exists = prev.some(fav => fav.id === item.id && fav.type === item.type);
      if (exists) return prev;
      
      return [...prev, item];
    });
  };

  // Remove item from favorites
  const removeFromFavorites = (id: number, type: 'movie' | 'tv') => {
    setFavorites(prev => prev.filter(fav => !(fav.id === id && fav.type === type)));
  };

  // Check if item is in favorites
  const isFavorite = (id: number, type: 'movie' | 'tv') => {
    return favorites.some(fav => fav.id === id && fav.type === type);
  };

  // Get favorites by type
  const getFavoritesByType = (type: 'movie' | 'tv') => {
    return favorites.filter(fav => fav.type === type);
  };

  // Clear all favorites
  const clearFavorites = () => {
    setFavorites([]);
  };

  // Get total count
  const getFavoritesCount = () => favorites.length;

  return {
    favorites,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    getFavoritesByType,
    clearFavorites,
    getFavoritesCount,
  };
}; 